import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import { Modal } from '../ui/modal';
import Button from '../ui/button/Button';
import DatePicker from '../form/date-picker';

interface CreditModalProps {
    credit?: any;
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
}

export default function CreditModal({ credit, isOpen, onClose, onSave }: CreditModalProps) {
    const [form, setForm] = useState({
        amount: 0,
        expiration_data: '',
        active: true
    });
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (credit) {
            setForm({
                amount: Number(credit.amount) || 0,
                // backend uses expiration_data
                expiration_data: credit.expiration_data ? new Date(credit.expiration_data).toISOString().split('T')[0] : '',
                active: !!credit.active
            });
        } else {
            setForm({
                amount: 0,
                expiration_data: '',
                active: true
            });
        }
    }, [credit, isOpen]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const payload = {
                amount: Number(form.amount),
                active: Boolean(form.active),
                expiration_data: form.expiration_data || undefined
            };

            if (credit?.id) {
                await client.postApiBillingCreditUpdate({
                    postApiBillingCreditUpdateRequest: {
                        id: credit.id,
                        ...payload
                    } as any
                });
            } else {
                await client.postApiBillingCreditCreate({
                    postApiBillingCreditCreateRequest: payload as any
                });
            }
            onSave();
            onClose();
        } catch (err: any) {
            console.error('Failed to save credit:', err);
            Swal.fire({
                title: 'Error',
                text: err.response?.data?.message || err.message || 'Failed to save',
                icon: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="max-w-md overflow-visible">
            <div className="flex flex-col h-full bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-visible">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {credit ? 'Edit Credit' : 'Add Credit'}
                    </h3>
                </div>

                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Amount *</label>
                        <input
                            type="number"
                            step="0.01"
                            value={form.amount}
                            onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) })}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                    </div>
                    <div className="relative z-20">
                        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Expiration Date</label>
                        <DatePicker
                            id="credit-modal-expiration-date"
                            placeholder="Select expiration date"
                            defaultDate={form.expiration_data ? new Date(form.expiration_data) : undefined}
                            onChange={(selectedDates) => {
                                if (selectedDates.length > 0) {
                                    const d = selectedDates[0];
                                    const year = d.getFullYear();
                                    const month = String(d.getMonth() + 1).padStart(2, '0');
                                    const day = String(d.getDate()).padStart(2, '0');
                                    const val = `${year}-${month}-${day}`;
                                    setForm(prev => ({ ...prev, expiration_data: val }));
                                } else {
                                    setForm(prev => ({ ...prev, expiration_data: '' }));
                                }
                            }}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={form.active}
                            onChange={e => setForm({ ...form, active: e.target.checked })}
                            className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                        />
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Active</label>
                    </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose} disabled={isSaving}>Cancel</Button>
                    <Button onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
